<template>
  <component
    :is="to && !disabled ? RouterLink : 'button'"
    v-bind="componentProps"
    v-tooltip
    :title="title || ''"
    :class="['ui-button', buttonClass]"
  >
    <span v-if="loading">
      <UiIcon class="animate-spin" icon="loader" :size="iconSize" />
      {{ loadingText || $t('feedback.await') }}
    </span>
    <template v-else>
      <div class="ctnt flex items-center justify-center gap-1">
        <UiIcon v-if="icon" :icon="icon" :size="iconSize" />
        <slot>{{ text }}</slot>
      </div>
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { RouterLink } from 'vue-router'

import UiIcon from './icon.vue'

defineOptions({
  inheritAttrs: false,
  name: 'UiButton'
})

const attrs = useAttrs()

const { type = 'primary', outline, round, rounded, disabled, to, loading, size = '' } = defineProps<{
  type?: 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'ghost' | 'link' | 'transparent' | 'glass'
  round?: boolean
  rounded?: boolean
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  icon?: string
  iconSize?: string | number
  outline?: boolean
  text?: string
  to?: string
  title?: string
}>()

const buttonClass = computed(() => {
  const classes: string[] = []
  if (['primary', 'info', 'success', 'warning', 'danger', 'ghost', 'link'].includes(type))
    classes.push(type)
  else if (type === 'glass')
    classes.push('b-glass')

  if (outline) classes.push('uib-outline')
  if (round) classes.push('aspect-square !rounded-full')
  else if (rounded) classes.push('!rounded-full')
  if (size) classes.push(size)

  return classes
})

const componentProps = computed(() => {
  if (to && !disabled) {
    return { to, ...attrs }
  }

  return { type: 'button', disabled: disabled || loading, ...attrs }
})
</script>

<style lang="scss">
.ui-button {
  @apply inline-block whitespace-nowrap cursor-pointer text-[#FDFDFD] rounded-md border-0 font-semibold transition-all duration-300 disabled:cursor-not-allowed disabled:text-[#79767A] dark:disabled:text-[#9C9C9C] focus:outline-1 focus:outline-offset-4;
  
  &:not(.uib-outline) {
    @apply disabled:bg-[#E6E1E6] dark:disabled:bg-[#414141];
  }

  &.uib-outline {
    @apply outline outline-1 -outline-offset-1;
  }

  &.primary:not(:disabled) {
    @apply bg-[--c-primary-600] hover:bg-[--c-primary-500] active:bg-[--c-primary-400] focus:outline-[--c-primary-300];
    &.uib-outline {
      @apply outline-[--c-primary-600] text-[--c-primary-600] bg-transparent hover:outline-[--c-primary-600] hover:bg-[--c-primary-600] hover:text-[--c-primary-at-600] active:bg-[--c-primary-400];
    }
    &:not(.uib-outline) {
      @apply text-[--c-primary-at-600];
    }
  }

  &.ghost {
    @apply bg-transparent hover:bg-black/10 active:bg-[#e5d0ff] active:text-neutral-900 text-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600;
  }

  &.info:not(:disabled) {
    @apply bg-[#1D9BF0] hover:bg-[#2CA9F7] active:bg-[#3AB8FF];
    &.uib-outline {
      @apply outline-[#1D9BF0] text-[#1D9BF0] bg-transparent hover:outline-[#2CA9F7] hover:bg-[#2CA9F7] hover:text-white active:bg-[#3AB8FF];
    }
  }

  &.danger:not(:disabled) {
    @apply bg-[#FF4980] hover:bg-[#FF6694] active:bg-[#FF749E];
    &.uib-outline {
      @apply outline-[#FF4980] text-[#FF4980] bg-transparent hover:outline-[#FF6694] hover:bg-[#FF6694] hover:text-white active:bg-[#FF749E];
    }
  }

  &.success:not(:disabled) {
    @apply bg-[#17AC6C] hover:bg-[#1bd183] active:bg-[#1dde8b];
    &.uib-outline {
      @apply outline-[#0E563A] dark:outline-[#17AC6C] dark:text-[#17AC6C] text-[#0E563A] bg-transparent hover:outline-[#13754f] hover:bg-[#13754f] hover:text-white active:bg-[#168a5d];
    }
  }

  &.warning:not(:disabled) {
    @apply bg-amber-500 hover:bg-amber-400 active:bg-amber-600;
    &.uib-outline {
      @apply outline-amber-500 text-amber-500 bg-transparent hover:outline-amber-400 hover:bg-amber-400 hover:text-white active:bg-amber-600;
    }
  }

  &.uib-outline:is(:disabled) {
    @apply outline-neutral-300 text-neutral-400;
  }

  &.link {
    @apply bg-transparent text-blue-600 hover:text-blue-800 dark:hover:text-blue-400;
  }

  &.b-glass {
    outline: 1px solid transparent;
    outline-offset: -1px;
    backdrop-filter: blur(8px);
    &:hover {
      backdrop-filter: blur(14px);
    }

    @apply bg-neutral-400/50 text-neutral-600 outline-neutral-300/20 hover:bg-neutral-200/60;
    @apply dark:bg-neutral-700/60 dark:text-neutral-300 dark:outline-neutral-400/20 dark:hover:bg-neutral-500/60;
  }

  &.sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  &.md {
    padding: 0.5rem 1rem;
    font-size: 1rem;
    line-height: 1.5rem
  }

  &.lg {
    padding: 0.75rem 1.5rem;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }

  &.xl {
    padding: 1rem 2rem;
    font-size: 1.25rem;
    line-height: 1.75rem;
  }
}

.btn-group {
  display: flex;
  align-items: center;
  justify-content: center;

  > .ui-button {
    border-radius: 0;

    &:first-of-type {
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
    }

    &:last-of-type {
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
    }
  }
}
</style>
